import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { ControlValueAccessor, ReactiveFormsModule } from '@angular/forms';
import { RADIO_BUTTON_OPTIONS_SHOW } from '@app/settings/settings.const';
import { OptionService } from '@app/services/option.service';
import { ChangeEffectAnimation } from '@app/animations/change-effect.animation';
import { HypoControlsBaseComponent } from '@app/components/form-components/hypo-control-base/hypo-control-base.component';
import { OptionInterface } from '@app/interfaces/option.interface';
import { debounceTime, first, takeUntil } from 'rxjs/operators';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { FlexModule } from '@angular/flex-layout';
import { NgClass } from '@angular/common';
import { MatError } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { TooltipComponent } from '@app/components/tooltip/tooltip.component';

@Component({
    selector: 'app-hypo-radio',
    templateUrl: './hypo-radio.component.html',
    styleUrls: ['./hypo-radio.component.scss'],
    animations: [ChangeEffectAnimation.FadeEffect],
    exportAs: 'hypoRadio',
    imports: [
        ReactiveFormsModule,
        MatRadioGroup,
        FlexModule,
        NgClass,
        MatRadioButton,
        MatError,
        MatIcon,
        TooltipComponent,
    ],
})
export class HypoRadioComponent extends HypoControlsBaseComponent implements ControlValueAccessor, OnInit, OnDestroy {
    private readonly optionService = inject(OptionService);

    @Input() label: string = '';
    @Input() toggleAbleOptions: boolean = false;
    @Input() showMoreOptions: boolean = false;
    @Input() listName: string = '';
    /** @deprecated Use itemClass instead **/
    @Input() buttonsFlex: number | string = 'initial';
    @Input() itemClass: string = '';
    @Input() showingList: OptionInterface[] = [];
    originalList: OptionInterface[] = [];

    constructor() {
        super();

        this.input.valueChanges.pipe(debounceTime(100), takeUntil(this.destroyed$)).subscribe(() => {
            if (this.showMoreOptions) {
                this.toggleMoreOptions(false);
            }
        });
    }

    public ngOnInit() {
        super.ngOnInit();
        this.afterInit();
    }

    public toggleMoreOptions(state: boolean = !this.showMoreOptions): void {
        if (this.toggleAbleOptions) {
            this.showMoreOptions = state;
            if (state) {
                this.showAllOptions();
            } else {
                this.showInitialOptions();
            }
        }
        if (!this.showingList?.length) {
            this.showAllOptions();
        }
    }

    private afterInit(): void {
        if (this.listName) {
            this.optionService.itemsLoadedObservable
                .pipe(
                    takeUntil(this.destroyed$),
                    first((loaded) => loaded)
                )
                .subscribe(() => {
                    this.originalList = this.optionService.getListByType(this.listName);
                    if (this.originalList?.length) {
                        if (this.toggleAbleOptions) {
                            this.showInitialOptions();
                        } else {
                            this.showAllOptions();
                        }
                    } else {
                        console.warn(`Hypo-radio has defined option list - ${this.listName} but could not be loaded.`);
                    }
                });
        }
    }

    private showAllOptions(): void {
        this.showingList = this.originalList.slice();
    }

    private showInitialOptions(): void {
        if (this.originalList?.length && this.originalList?.length <= RADIO_BUTTON_OPTIONS_SHOW) {
            this.toggleAbleOptions = false;
            this.showAllOptions();
            return;
        }
        this.showingList = this.originalList.slice(0, RADIO_BUTTON_OPTIONS_SHOW);
        const selectedValueIndex = this.originalList.findIndex((a) => a.value === this.input.value);
        if (selectedValueIndex !== -1 && selectedValueIndex >= RADIO_BUTTON_OPTIONS_SHOW) {
            this.showingList.unshift(this.originalList[selectedValueIndex]);
        }
    }
}
